import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Clock, Phone, MapPin, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const PRIO_COLORS = {
  critical: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-amber-400",
  low: "border-l-slate-500",
};

export default function FsmTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [tech, setTech] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("active");

  async function load() {
    const techs = await api.get("/technicians");
    const me = techs.data.find((t) => (t.email || "").toLowerCase() === (user?.email || "").toLowerCase());
    setTech(me);
    if (me) {
      const t = await api.get(`/service-tickets?technician_id=${me.id}`);
      setTickets(t.data);
      const s = await api.get(`/dashboard/technician/${me.id}`);
      setStats(s.data);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const filtered = tickets.filter((t) => {
    if (filter === "active") return ["open", "in_progress", "on_hold"].includes(t.status);
    return t.status === "closed";
  });

  async function changeStatus(t, status) {
    try {
      await api.put(`/service-tickets/${t.id}/status?status=${status}`);
      toast.success(`Ticket ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  function slaText(t) {
    const created = new Date(t.created_at);
    const now = new Date();
    const hrs = (now - created) / 3600000;
    const remaining = t.sla_hours - hrs;
    if (t.status === "closed") return "Closed";
    if (remaining < 0) return `${Math.abs(Math.round(remaining))}h breach`;
    return `${Math.round(remaining)}h left`;
  }

  return (
    <div className="px-5 py-6 space-y-5">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">{tech?.name || user?.name}</div>
        <h2 className="font-display text-3xl font-bold tracking-tighter mt-1">Today's worklist.</h2>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-[#1E293B] bg-[#0A1128] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Open</div>
            <div className="font-display text-3xl font-bold text-[#D4FF00] mt-1">{stats.open_tickets}</div>
          </div>
          <div className="border border-[#1E293B] bg-[#0A1128] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Closed</div>
            <div className="font-display text-3xl font-bold mt-1">{stats.closed_tickets}</div>
          </div>
          <div className="border border-[#1E293B] bg-[#0A1128] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Avg SLA</div>
            <div className="font-display text-3xl font-bold mt-1">{stats.sla_avg_hours}<span className="text-sm text-[#94A3B8] font-mono">h</span></div>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-[#1E293B]">
        {[
          ["active", "Active"],
          ["closed", "History"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`fsm-filter-${k}`}
            className={"px-4 py-3 font-mono text-xs uppercase tracking-widest border-b-2 " +
              (filter === k ? "border-[#FF5C00] text-white" : "border-transparent text-[#94A3B8]")}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((t) => (
          <div key={t.id} className={"border border-[#1E293B] bg-[#0A1128] border-l-4 p-4 " + PRIO_COLORS[t.priority]} data-testid={`fsm-ticket-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">{t.ticket_number} · {t.type.replace("_", " ")}</div>
                <div className="font-display text-lg font-semibold mt-1 leading-tight">{t.title}</div>
              </div>
              <div className={"font-mono text-xs " + (slaText(t).includes("breach") ? "text-red-400" : "text-[#D4FF00]")}>
                <Clock className="w-3 h-3 inline mr-1" />{slaText(t)}
              </div>
            </div>

            {t.is_billable && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#FF5C00] text-white px-2 py-1"
                data-testid={`fsm-bill-${t.id}`}>
                💰 Bill the customer · Out of warranty
              </div>
            )}
            {t.warranty_status === "in_warranty" && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#D4FF00]/20 text-[#D4FF00] px-2 py-1 border border-[#D4FF00]/40">
                🛡 In warranty · Free service
              </div>
            )}

            <div className="text-xs text-[#94A3B8] mt-2 line-clamp-2">{t.description}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono">
              <div className="flex items-center gap-1 text-[#94A3B8]"><Phone className="w-3 h-3" />{t.customer_snapshot?.phone || "—"}</div>
              <div className="flex items-center gap-1 text-[#94A3B8] truncate"><MapPin className="w-3 h-3" />{t.customer_snapshot?.address || "—"}</div>
            </div>
            {t.serial_code && (
              <div className="text-xs font-mono mt-2 text-[#94A3B8]">Serial: <span className="text-white">{t.serial_code}</span></div>
            )}
            {t.status !== "closed" && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {t.status !== "in_progress" && (
                  <button onClick={() => changeStatus(t, "in_progress")} data-testid={`fsm-start-${t.id}`}
                    className="bg-[#FF5C00] text-white px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
                    Start
                  </button>
                )}
                <button onClick={() => changeStatus(t, "closed")} data-testid={`fsm-close-${t.id}`}
                  className="border border-[#D4FF00] text-[#D4FF00] px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
                  <CheckCircle2 className="w-3 h-3 inline mr-1" /> Mark closed
                </button>
                <button onClick={() => changeStatus(t, "on_hold")}
                  className="border border-[#1E293B] text-[#94A3B8] px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
                  Hold
                </button>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="border border-dashed border-[#1E293B] p-8 text-center text-sm text-[#94A3B8]">
            {filter === "active" ? "All clear. Take a breath. ☀️" : "No closed tickets yet."}
          </div>
        )}
      </div>
    </div>
  );
}
